// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import PropTypes from "prop-types";
import { useState, useEffect } from "react"; // ← Agregar estos imports

// Material Dashboard 2 React components
import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";

// Material Dashboard 2 React example components
import DataTable from "shared/components/Tables/DataTable";
import { useTablaConciliacion } from "./useTablaConciliacion";

function TablaConciliacion({ nombreUseCase, titulo }) {
  // Estado para el progreso simulado
  const [simulatedProgress, setSimulatedProgress] = useState(0);

  const {
    conciliaciones,
    estados,
    comercios,
    selectedConciliacion,
    setSelectedConciliacion,
    selectedEstado,
    setSelectedEstado,
    selectedComercio,
    setSelectedComercio,
    loading,
    stats,
    rowsNovedad,
    rowsComision,
    rowsDetalle,
    rows,
    handleFiltrar,
    handleLimpiarFiltros,
    rowsResumenComercios,
  } = useTablaConciliacion(nombreUseCase);

  // ==================== EFECTO PARA EL PROGRESO SIMULADO ====================
  useEffect(() => {
    let interval;

    if (loading) {
      // Reiniciamos el progreso cuando empieza a cargar
      setSimulatedProgress(0);

      // Duración total estimada: 8 segundos (8000ms)
      const totalTime = 8000;
      const updateInterval = 100; // Actualizar cada 100ms para suavidad
      const increments = totalTime / updateInterval;
      const progressPerIncrement = 100 / increments;

      interval = setInterval(() => {
        setSimulatedProgress((prev) => {
          const newProgress = prev + progressPerIncrement;
          // Nos detenemos en 95% hasta que loading sea false
          return newProgress >= 95 ? 95 : newProgress;
        });
      }, updateInterval);
    } else {
      // Cuando termina de cargar, completamos al 100%
      setSimulatedProgress(100);
      // Y después de un momento volvemos a 0
      setTimeout(() => setSimulatedProgress(0), 500);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [loading]);

  // ==================== CONFIGURACIÓN DE COLUMNAS ====================
  const columns = [
    { Header: "Conciliación", accessor: "conciliacion", width: "40%" },
    { Header: "Estado Conciliación", accessor: "estadoConciliacion", width: "15%" },
    { Header: "Cantidad Tx", accessor: "cantidadTX", width: "10%" },
    { Header: "Valor Transacciones Comercio", accessor: "valorTransacciones", width: "15%" },
  ];

  const columnsNovedad = [
    { Header: "Tipo Conciliación", accessor: "tipoConciliacion", width: "70%" },
    { Header: "Novedad en CUFE", accessor: "cantidad", width: "30%" },
  ];

  const columnsComision = [
    { Header: "LLave Cobro", accessor: "llave_cobro", width: "30%" },
    { Header: "Valor Transacciones Comercio", accessor: "valorServicio", width: "20%" },
    { Header: "Comisión 3.1%", accessor: "comision", width: "10%" },
    { Header: "IVA 19%", accessor: "ivaComision", width: "10%" },
    { Header: "Retefuente 11%", accessor: "retefuente", width: "10%" },
    { Header: "Reteica 0%", accessor: "reteica", width: "10%" },
    { Header: "Reteiva 15%", accessor: "reteiva", width: "10%" },
  ];

  const columnsDetalle = [
    { Header: "Codigo Paso", accessor: "codigopaso", width: "15%" },
    { Header: "Placa", accessor: "placa", width: "10%" },
    { Header: "Valor Pagado", accessor: "valorPagado", width: "10%" },
    { Header: "Valor Descuento", accessor: "valorDescuento", width: "10%" },
    { Header: "Total Debito", accessor: "notaDebito", width: "10%" },
    { Header: "Total Nota Credito", accessor: "notaCredito", width: "10%" },
    { Header: "Valor a Pagar", accessor: "valorPagar", width: "10%" },
    { Header: "Cant ajustes", accessor: "ajustes", width: "5%" },
    { Header: "Comercio", accessor: "comercio", width: "10%" },
    { Header: "Concesión", accessor: "concesion", width: "10%" },
    { Header: "Fecha Paso", accessor: "fechaPaso", width: "15%" },
    { Header: "Hora Paso", accessor: "horaPaso", width: "10%" },
    { Header: "N° Factura", accessor: "factura", width: "10%" },
    { Header: "CUFE", accessor: "cufe", width: "10%" },
  ];

  const columnsResumenComercios = [
    { Header: "Comercio", accessor: "comercio", width: "40%" },
    { Header: "Cantidad", accessor: "cantidad", width: "30%", align: "right" },
    { Header: "Valor a Pagar", accessor: "valorPagar", width: "30%", align: "right" },
  ];

  // ==================== PREPARACIÓN DE FILAS ====================
  const processedRows = rows.map((conciliacion) => ({
    conciliacion: (
      <MDBox display="flex" alignItems="center" lineHeight={1}>
        {conciliacion.estadoConciliacion === "Sin novedad" ? (
          <CheckCircleIcon color="success" fontSize="small" />
        ) : (
          <ErrorIcon color="error" fontSize="small" />
        )}
        <MDBox ml={2} lineHeight={1}>
          <MDTypography display="block" variant="button" fontWeight="medium">
            {conciliacion.conciliacion.name}
          </MDTypography>
          <MDTypography variant="caption">{conciliacion.conciliacion.referencia}</MDTypography>
        </MDBox>
      </MDBox>
    ),

    cantidadTX: (
      <MDBox lineHeight={1} textAlign="left">
        <MDTypography display="block" variant="caption" color="text" fontWeight="medium">
          {conciliacion.cantidadTX.toLocaleString()}
        </MDTypography>
        <MDTypography variant="caption">transacciones</MDTypography>
      </MDBox>
    ),

    valorTransacciones: (
      <MDBox lineHeight={1} textAlign="left">
        <MDTypography display="block" variant="caption" color="text" fontWeight="medium">
          ${conciliacion.valorTransacciones.toLocaleString("es-CO")}
        </MDTypography>
        <MDTypography variant="caption">COP</MDTypography>
      </MDBox>
    ),

    estadoConciliacion: (
      <MDBox display="flex" alignItems="center">
        <MDTypography
          component="span"
          variant="caption"
          color="text"
          fontWeight="medium"
          sx={{
            color:
              conciliacion.estadoConciliacion === "Sin novedad" ? "success.main" : "error.main",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Icon
            sx={{
              color:
                conciliacion.estadoConciliacion === "Sin novedad" ? "success.main" : "error.main",
              mr: 0.5,
            }}
          >
            circle
          </Icon>
          {conciliacion.estadoConciliacion}
        </MDTypography>
      </MDBox>
    ),
  }));

  // ==================== PREPARACIÓN DE FILAS RESUMEN COMERCIOS ====================
  const processedRowsResumenComercios = rowsResumenComercios.map((comercio) => ({
    comercio: (
      <MDTypography variant="button" fontWeight="medium">
        {comercio.comercio}
      </MDTypography>
    ),
    cantidad: (
      <MDBox textAlign="right">
        <MDTypography variant="caption" fontWeight="medium">
          {comercio.cantidad.toLocaleString()}
        </MDTypography>
      </MDBox>
    ),
    valorPagar: (
      <MDBox textAlign="right">
        <MDTypography variant="caption" fontWeight="medium" color="success">
          ${comercio.valorPagar.toLocaleString("es-CO")}
        </MDTypography>
        <MDTypography variant="caption" display="block" color="text">
          COP
        </MDTypography>
      </MDBox>
    ),
  }));

  // ==================== RENDERIZADO DE CARGA ====================
  if (loading) {
    const progressRounded = Math.round(simulatedProgress);

    return (
      <MDBox
        pt={8}
        pb={8}
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
        minHeight="80vh"
        sx={{
          background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
          borderRadius: 3,
        }}
      >
        {/* Contenedor principal con efecto de tarjeta flotante */}
        <MDBox
          sx={{
            background: "white",
            borderRadius: 4,
            p: 6,
            boxShadow: "0 20px 60px rgba(0,0,0,0.1)",
            border: "1px solid rgba(255,255,255,0.3)",
            backdropFilter: "blur(10px)",
            textAlign: "center",
            maxWidth: "500px",
            width: "90%",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: "linear-gradient(90deg, #4CAF50, #8BC34A, #CDDC39)",
              animation: "loadingBar 2s infinite ease-in-out",
            },
          }}
        >
          {/* Icono animado con partículas */}
          <MDBox
            sx={{
              position: "relative",
              display: "inline-block",
              mb: 3,
            }}
          >
            <MDBox
              sx={{
                width: 100,
                height: 100,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #4CAF50, #8BC34A)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto",
                position: "relative",
                animation: "float 3s ease-in-out infinite",
                boxShadow: "0 10px 30px rgba(76, 175, 80, 0.3)",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  width: "120%",
                  height: "120%",
                  borderRadius: "50%",
                  border: "2px solid rgba(76, 175, 80, 0.1)",
                  animation: "pulse 2s infinite",
                },
                "&::after": {
                  content: '""',
                  position: "absolute",
                  width: "140%",
                  height: "140%",
                  borderRadius: "50%",
                  border: "1px solid rgba(76, 175, 80, 0.05)",
                  animation: "pulse 3s infinite 0.5s",
                },
              }}
            >
              <Icon
                sx={{
                  fontSize: 48,
                  color: "white",
                  zIndex: 2,
                  position: "relative",
                }}
              >
                analytics
              </Icon>
            </MDBox>

            {/* Partículas flotantes */}
            {[1, 2, 3, 4, 5].map((item) => (
              <MDBox
                key={item}
                sx={{
                  position: "absolute",
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: `rgba(76, 175, 80, ${0.3 + item * 0.1})`,
                  top: `${20 + item * 10}%`,
                  left: `${item * 20}%`,
                  animation: `floatParticle 3s infinite ${item * 0.2}s`,
                }}
              />
            ))}
          </MDBox>

          {/* Texto principal */}
          <MDTypography
            variant="h3"
            fontWeight="bold"
            color="dark"
            gutterBottom
            sx={{
              background: "linear-gradient(135deg, #2E7D32, #4CAF50)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              mb: 2,
            }}
          >
            Procesando Datos
          </MDTypography>

          {/* Subtítulo dinámico */}
          <MDTypography variant="h6" color="text" fontWeight="medium" gutterBottom sx={{ mb: 3 }}>
            {titulo || "Reporte de Conciliaciones"}
          </MDTypography>

          {/* Descripción con animación de puntos */}
          <MDTypography
            variant="body1"
            color="text"
            textAlign="center"
            sx={{
              mb: 4,
              minHeight: "48px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <span>
              Cargando información de conciliaciones
              <MDBox
                component="span"
                sx={{
                  animation: "dots 1.5s infinite steps(4)",
                  display: "inline-block",
                  width: "20px",
                  textAlign: "left",
                }}
              >
                ...
              </MDBox>
            </span>
          </MDTypography>

          {/* Progress bar moderna que muestra el progreso real */}
          <MDBox
            sx={{
              width: "100%",
              height: 6,
              backgroundColor: "grey.100",
              borderRadius: 3,
              overflow: "hidden",
              mb: 3,
              position: "relative",
            }}
          >
            <MDBox
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                height: "100%",
                width: `${simulatedProgress}%`,
                background: "linear-gradient(90deg, #4CAF50, #8BC34A)",
                borderRadius: 3,
                transition: "width 0.3s ease",
              }}
            />
          </MDBox>

          {/* Indicador de progreso con estadísticas dinámicas */}
          <MDBox display="flex" justifyContent="space-around" alignItems="center" sx={{ mb: 3 }}>
            {[
              {
                label: "Transacciones",
                value: simulatedProgress >= 30 ? "✓" : "...",
                completed: simulatedProgress >= 30,
              },
              {
                label: "Comercios",
                value: simulatedProgress >= 60 ? "✓" : "...",
                completed: simulatedProgress >= 60,
              },
              {
                label: "Conciliaciones",
                value: `${progressRounded}%`,
                completed: simulatedProgress >= 95,
              },
            ].map((item, index) => (
              <MDBox key={index} textAlign="center">
                <MDTypography
                  variant="button"
                  fontWeight="bold"
                  color={item.completed ? "success" : "info"}
                >
                  {item.value}
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  {item.label}
                </MDTypography>
              </MDBox>
            ))}
          </MDBox>

          {/* Spinner moderno con porcentaje */}
          <MDBox sx={{ position: "relative", display: "inline-flex" }}>
            <CircularProgress
              size={60}
              thickness={4}
              sx={{
                color: "success.main",
              }}
            />
            <MDBox
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: "absolute",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <MDTypography
                variant="caption"
                component="div"
                color="success.main"
                fontWeight="bold"
              >
                {progressRounded}%
              </MDTypography>
            </MDBox>
          </MDBox>

          {/* Mensaje de ayuda */}
          <MDTypography
            variant="caption"
            color="text"
            sx={{
              display: "block",
              mt: 3,
              opacity: 0.7,
              fontStyle: "italic",
            }}
          >
            Esto puede tomar unos segundos. Por favor no cierre la ventana.
          </MDTypography>
        </MDBox>

        {/* Definición de animaciones CSS */}
        <style>{`
          @keyframes loadingBar {
            0% { transform: translateX(-100%); }
            50% { transform: translateX(0%); }
            100% { transform: translateX(100%); }
          }
          
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
          }
          
          @keyframes floatParticle {
            0%, 100% { transform: translateY(0px) translateX(0px); opacity: 0; }
            50% { transform: translateY(-20px) translateX(10px); opacity: 1; }
          }
          
          @keyframes pulse {
            0% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.1); opacity: 0.7; }
            100% { transform: scale(1); opacity: 1; }
          }
          
          @keyframes dots {
            0%, 20% { opacity: 0; }
            40% { opacity: 1; }
            60% { opacity: 1; }
            80%, 100% { opacity: 1; }
          }
        `}</style>
      </MDBox>
    );
  }

  // ==================== RENDERIZADO PRINCIPAL ====================
  return (
    <MDBox pt={-2} pb={3}>
      <Grid container spacing={2}>
        <Grid item xs={12} lg={12}>
          <Card>
            {/* ENCABEZADO PRINCIPAL */}
            <MDBox
              mx={1}
              mt={1}
              py={1}
              px={2}
              variant="gradient"
              bgColor="success"
              borderRadius="lg"
              coloredShadow="info"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <MDTypography variant="h6" color="white">
                REPORTE DE CONCILIACIONES - {titulo.toUpperCase()}
              </MDTypography>
              <MDTypography variant="button" color="white" fontWeight="medium">
                {new Date().toLocaleDateString()}
              </MDTypography>
            </MDBox>

            {/* SECCIÓN DE FILTROS */}
            <MDBox mt={3} mb={2} px={2}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={12} md={4}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel id="conciliacion-label">Seleccione una Conciliación</InputLabel>
                    <Select
                      labelId="conciliacion-label"
                      label="Seleccione una Conciliación"
                      value={selectedConciliacion}
                      onChange={(e) => setSelectedConciliacion(e.target.value)}
                      sx={{ height: 45 }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: 150,
                            overflow: "auto",
                            "&::-webkit-scrollbar": {
                              width: "8px",
                            },
                            "&::-webkit-scrollbar-track": {
                              background: "#f1f1f1",
                              borderRadius: "4px",
                            },
                            "&::-webkit-scrollbar-thumb": {
                              background: "#888",
                              borderRadius: "4px",
                            },
                            "&::-webkit-scrollbar-thumb:hover": {
                              background: "#555",
                            },
                          },
                        },
                      }}
                    >
                      <MenuItem value="">
                        <em>Todas las conciliaciones</em>
                      </MenuItem>
                      {conciliaciones.map((conciliacion) => (
                        <MenuItem key={conciliacion.id} value={conciliacion.nombre}>
                          {conciliacion.nombre}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={12} md={3}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel id="estado-label">Estado de Conciliación</InputLabel>
                    <Select
                      labelId="estado-label"
                      label="Estado de Conciliación"
                      value={selectedEstado}
                      onChange={(e) => setSelectedEstado(e.target.value)}
                      sx={{ height: 45 }}
                    >
                      <MenuItem value="">
                        <em>Todos los estados</em>
                      </MenuItem>
                      {estados.map((estado) => (
                        <MenuItem key={estado.id} value={estado.nombre}>
                          {estado.nombre}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={12} md={2}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel id="comercio-label">Comercio</InputLabel>
                    <Select
                      labelId="comercio-label"
                      label="Comercio"
                      value={selectedComercio}
                      onChange={(e) => setSelectedComercio(e.target.value)}
                      sx={{ height: 45 }}
                    >
                      <MenuItem value="">
                        <em>Todos los comercios</em>
                      </MenuItem>
                      {comercios.map((comercio) => (
                        <MenuItem key={comercio.nombre} value={comercio.nombre}>
                          {comercio.nombre}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* BOTONES DE ACCIÓN */}
                <Grid item xs={6} sm={6} md={1.5}>
                  <MDButton
                    variant="gradient"
                    color="success"
                    fullWidth
                    sx={{ height: 45, boxShadow: "none" }}
                    onClick={handleFiltrar}
                  >
                    <Icon>search</Icon>
                    &nbsp;Filtrar
                  </MDButton>
                </Grid>
                <Grid item xs={6} sm={6} md={1.5}>
                  <MDButton
                    variant="outlined"
                    color="dark"
                    fullWidth
                    sx={{ height: 45, boxShadow: "none" }}
                    onClick={handleLimpiarFiltros}
                  >
                    <Icon>refresh</Icon>
                    &nbsp;Limpiar
                  </MDButton>
                </Grid>
              </Grid>
            </MDBox>

            {/* ESTADÍSTICAS RESUMEN */}
            <MDBox px={2} pb={2}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4} md={4}>
                  <MDBox
                    borderRadius="lg"
                    bgColor="info"
                    p={2}
                    color="white"
                    textAlign="center"
                    sx={{ boxShadow: 2 }}
                  >
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      display="block"
                      color="white"
                    >
                      Cantidad Conciliaciones
                    </MDTypography>
                    <MDTypography variant="h5" fontWeight="bold" color="white">
                      {stats.conciliacionesConNovedad + stats.conciliacionesSinNovedad}
                    </MDTypography>
                  </MDBox>
                </Grid>
                <Grid item xs={12} sm={4} md={4}>
                  <MDBox
                    borderRadius="lg"
                    bgColor="info"
                    p={2}
                    textAlign="center"
                    sx={{ boxShadow: 2 }}
                  >
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      display="block"
                      color="white"
                    >
                      Cantidad Transacciones
                    </MDTypography>
                    <MDTypography variant="h5" fontWeight="bold" color="white">
                      {stats.totalTransacciones.toLocaleString()}
                    </MDTypography>
                  </MDBox>
                </Grid>
                <Grid item xs={12} sm={4} md={4}>
                  <MDBox
                    borderRadius="lg"
                    bgColor="success"
                    p={2}
                    color="white"
                    textAlign="center"
                    sx={{ boxShadow: 2 }}
                  >
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      display="block"
                      color="white"
                    >
                      $ Transacciones
                    </MDTypography>
                    <MDTypography variant="h5" fontWeight="bold" color="white">
                      ${stats.totalValorTransacciones.toLocaleString("es-CO")}
                    </MDTypography>
                  </MDBox>
                </Grid>
              </Grid>
            </MDBox>

            {/* TABLA PRINCIPAL DE CONCILIACIONES */}
            <MDBox px={2} pb={2}>
              <Card>
                <MDBox pt={2}>
                  <DataTable
                    table={{ columns, rows: processedRows }}
                    isSorted={true}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                </MDBox>
              </Card>
            </MDBox>

            {/* TABLA RESUMEN POR COMERCIOS */}
            <MDBox px={2} pb={2}>
              <Card>
                <MDBox
                  mx={1}
                  mt={1}
                  py={1}
                  px={2}
                  variant="gradient"
                  bgColor="success"
                  borderRadius="lg"
                  coloredShadow="info"
                >
                  <MDTypography variant="h6" color="white">
                    RESUMEN POR COMERCIOS - {titulo.toUpperCase()}
                  </MDTypography>
                </MDBox>
                <MDBox pt={2}>
                  <DataTable
                    table={{
                      columns: columnsResumenComercios,
                      rows: processedRowsResumenComercios,
                    }}
                    isSorted={true}
                    entriesPerPage={false}
                    showTotalEntries={true}
                    noEndBorder
                  />
                </MDBox>
              </Card>
            </MDBox>

            {/* TABLA DE DETALLE DE TRANSACCIONES */}
            <MDBox px={2} pb={2}>
              <Card>
                <MDBox
                  mx={1}
                  mt={1}
                  py={1}
                  px={2}
                  variant="gradient"
                  bgColor="success"
                  borderRadius="lg"
                  coloredShadow="info"
                >
                  <MDTypography variant="h6" color="white">
                    DETALLE DE TRANSACCIONES - {titulo.toUpperCase()}
                  </MDTypography>
                </MDBox>
                <MDBox pt={2}>
                  <DataTable
                    table={{ columns: columnsDetalle, rows: rowsDetalle }}
                    canSearch={true}
                    isSorted={true}
                    entriesPerPage={true}
                    showTotalEntries={true}
                    noEndBorder
                  />
                </MDBox>
              </Card>
            </MDBox>

            {/* TABLA DE CONSOLIDADO DE COMISIONES */}
            <MDBox px={2} pb={2}>
              <Card>
                <MDBox
                  mx={1}
                  mt={1}
                  py={1}
                  px={2}
                  variant="gradient"
                  bgColor="success"
                  borderRadius="lg"
                  coloredShadow="info"
                >
                  <MDTypography variant="h6" color="white">
                    CONSOLIDADO DE COMISIONES - {titulo.toUpperCase()}
                  </MDTypography>
                </MDBox>
                <MDBox pt={2}>
                  <DataTable
                    table={{ columns: columnsComision, rows: rowsComision }}
                    isSorted={true}
                    entriesPerPage={false}
                    showTotalEntries={true}
                    noEndBorder
                  />
                </MDBox>
              </Card>
            </MDBox>

            {/* TABLA DE CONCILIACIONES CON NOVEDAD */}
            {rowsNovedad.length > 0 && (
              <MDBox px={2} pb={2}>
                <Card>
                  <MDBox
                    mx={1}
                    mt={1}
                    py={1}
                    px={2}
                    variant="gradient"
                    bgColor="error"
                    borderRadius="lg"
                    coloredShadow="info"
                  >
                    <MDTypography variant="h6" color="white">
                      CONCILIACIONES CON NOVEDAD - {titulo.toUpperCase()}
                    </MDTypography>
                  </MDBox>
                  <MDBox pt={2}>
                    <DataTable
                      table={{ columns: columnsNovedad, rows: rowsNovedad }}
                      isSorted={true}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      noEndBorder
                    />
                  </MDBox>
                </Card>
              </MDBox>
            )}
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
}

TablaConciliacion.propTypes = {
  nombreUseCase: PropTypes.string.isRequired,
  titulo: PropTypes.string.isRequired,
};

export default TablaConciliacion;
